import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { UnderMaintenancePage } from "@/components/public/UnderMaintenancePage";
import { supabase } from "@/integrations/backend/client";
import { trackFunnelStep } from "@/lib/funnelTracking";
import { getBioTheme, setBioTheme, getInstantBg } from '@/lib/bioThemeCache';
import { PageSettings } from "@/types/editor";
import { getBackgroundStyle, getPresetAnimationClass, getVideoOverlayStyle } from "@/lib/background";
import { resolveButtonColors } from "@/lib/linkStyle";
import { SeoHead } from "@/components/seo/SeoHead";
import { EmailCollectionForm } from "@/components/landing/EmailCollectionForm";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import LiquidIridescentBackground from "@/components/background/LiquidIridescentBackground";
import WebGLIridescentBackground from "@/components/background/WebGLIridescentBackground";
import WebGLAuroraBorealisBackground from "@/components/background/WebGLAuroraBorealisBackground";
import WebGLMeshGradientBackground from '@/components/background/WebGLMeshGradientBackground';
import WebGLLiquidMarbleBackground from "@/components/background/WebGLLiquidMarbleBackground";
import WebGLPortalBackground from "@/components/background/WebGLPortalBackground";
import WebGLPoolBackground from "@/components/background/WebGLPoolBackground";
import WebGLCyberfluidBackground from "@/components/background/WebGLCyberfluidBackground";
import VideoBackground from "@/components/background/VideoBackground";

interface ContactProfile {
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url?: string | null;
  is_published?: boolean;
  user_id?: string;
}

export default function PublicContactPage() {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { username } = useParams<{ username: string }>();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get("preview");

  const [profile, setProfile] = useState<ContactProfile | null>(null);
  const [settings, setSettings] = useState<PageSettings | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [profileAvatarError, setProfileAvatarError] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!username) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        let resolvedProfile: ContactProfile | null = null;
        let resolvedUserId: string | null = null;

        if (previewToken) {
          const { data: tokenUserId, error: tokenError } = await supabase.rpc("validate_preview_token", {
            _token: previewToken,
          });

          if (!tokenError && tokenUserId) {
            resolvedUserId = tokenUserId as string;
            const { data: privateProfile } = await supabase
              .from("profiles")
              .select("username, display_name, bio, is_published, avatar_url")
              .eq("user_id", resolvedUserId)
              .eq("username", username)
              .maybeSingle();

            if (privateProfile) {
              resolvedProfile = {
                ...privateProfile,
                user_id: resolvedUserId,
              };
              setIsPreview(true);
            }
          }
        }

        if (!resolvedProfile) {
          const { data: publicProfile } = await supabase
            .from("profiles_public")
            .select("username, display_name, bio, avatar_url")
            .eq("username", username)
            .maybeSingle();

          if (publicProfile) {
            resolvedProfile = publicProfile;
          }
        }

        if (!resolvedProfile) {
          // Check if username exists but page is unpublished (maintenance mode)
          const { data: statusRows } = await (supabase.rpc as any)('get_page_status', { p_username: username });
          const status = statusRows?.[0];
          if (status?.page_exists && !status.is_published) {
            setIsUnderMaintenance(true);
          } else {
            setNotFound(true);
          }
          setLoading(false);
          return;
        }

        if (!resolvedUserId) {
          const { data: idData } = await supabase.rpc("get_user_id_by_username", { _username: username });
          resolvedUserId = idData as string | null;
        }

        if (!resolvedUserId) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const { data: settingsData } = await supabase
          .from("page_settings")
          .select("*")
          .eq("user_id", resolvedUserId)
          .maybeSingle();

        if (!settingsData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setProfile({
          ...resolvedProfile,
          user_id: resolvedUserId,
        });
        setUserId(resolvedUserId);
        setSettings(settingsData as unknown as PageSettings);

        // Cache theme for instant loading-state backgrounds
        setBioTheme(username!, {
          settings: settingsData as unknown as PageSettings,
          profile: {
            username: username!,
            display_name: resolvedProfile.display_name ?? null,
            bio: resolvedProfile.bio ?? null,
            avatar_url: resolvedProfile.avatar_url ?? null,
          },
        });

        // Track funnel step for real public visits only
        if (!previewToken) {
          trackFunnelStep(resolvedUserId, 'lead', 'lead_form_view').catch(() => {});
        }
      } catch (error) {
        console.error("Error loading contact page:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [username, previewToken]);

  if (loading) {
    const cached = getBioTheme(username ?? '');
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.12 } }}
        transition={{ duration: 0.22, ease: 'easeOut', delay: 0.15 }}
        className="min-h-screen flex items-center justify-center"
        style={cached ? getInstantBg(cached.settings) : { background: 'hsl(var(--background))' }}
      >
        <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" style={cached ? { color: cached.settings.text_color } : {}} />
      </motion.div>
    );
  }

  if (isUnderMaintenance) {
    return <UnderMaintenancePage username={username} />;
  }

  if (notFound || !profile || !settings || !userId || !settings.email_collection_enabled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-2">{t("Página de contato indisponível", "Contact page not available")}</h1>
        <p className="text-muted-foreground mb-6">{t("Este formulário de contato não está habilitado para este perfil.", "This contact form is not enabled for this profile.")}</p>
        <Link to={`/${username}`} className="text-primary hover:underline">
          {t("Voltar para a bio page", "Back to bio page")}
        </Link>
      </div>
    );
  }

  const backToBioPath = previewToken
    ? `/${username}?preview=${encodeURIComponent(previewToken)}`
    : `/${username}`;
  const shouldNoIndex = isPreview || profile.is_published === false;

  // ── Theme helpers ──────────────────────────────────────────────────────────
  const { btnBg } = resolveButtonColors(settings);
  const presetAnim = getPresetAnimationClass(settings);
  const isLiquid    = presetAnim === 'liquid-iridescent-bg';
  const isWebGLIrid = presetAnim === 'webgl-iridescent-bg';
  const isAurora    = presetAnim === 'webgl-aurora-borealis-bg';
  const isPortal    = presetAnim === 'webgl-portal';
  const isPool      = presetAnim === 'webgl-pool';
  const isCyber     = presetAnim === 'webgl-cyber-fluid';
  const isMarble    = presetAnim?.startsWith('webgl-liquid-') || false;
  const isMesh      = presetAnim?.startsWith('webgl-mesh-') || false;
  const meshVariant = isMesh ? presetAnim?.replace('webgl-mesh-', '') : undefined;
  const marbleVariant = isMarble ? presetAnim?.replace('webgl-liquid-', '') : undefined;
  const isVideo     = settings.background_mode === 'custom' && !!settings.background_video_url;
  const showCssBg   = !isLiquid && !isWebGLIrid && !isAurora && !isPortal && !isPool && !isCyber && !isMarble && !isMesh;

  const textStyle: React.CSSProperties = { color: settings.text_color };

  // Determine if text is light (needs dark glass) or dark (needs light glass)
  const textColorIsLight = (() => {
    const hex = (settings.text_color ?? '#ffffff').replace('#', '');
    if (hex.length < 6) return true;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
  })();
  const glassBgStrong = textColorIsLight ? 'rgba(0,0,0,0.60)' : 'rgba(255,255,255,0.78)';

  const glassCard: React.CSSProperties = {
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    backgroundColor: glassBgStrong,
    border: `1px solid ${settings.text_color}1F`,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.12 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="relative min-h-screen overflow-hidden"
      style={{ fontFamily: settings.font_family }}
    >
      <SeoHead
        title={`${profile.display_name || `@${username}`} | ${t("Contato", "Contact")}`}
        description={
          settings.email_collection_description ||
          t(`Entre em contato com ${profile.display_name || `@${username}`}.`, `Get in touch with ${profile.display_name || `@${username}`}.`)
        }
        path={`/${username}/contact`}
        noindex={shouldNoIndex}
      />

      <link
        href={`https://fonts.googleapis.com/css2?family=${settings.font_family.replace(" ", "+")}:wght@400;500;600;700&display=swap`}
        rel="stylesheet"
      />

      {/* Background layer */}
      {isLiquid    && <div className="absolute inset-0"><LiquidIridescentBackground /></div>}
      {isWebGLIrid && <div className="absolute inset-0"><WebGLIridescentBackground /></div>}
      {isAurora    && <div className="absolute inset-0"><WebGLAuroraBorealisBackground /></div>}
      {isPortal    && <div className="absolute inset-0"><WebGLPortalBackground /></div>}
      {isPool      && <div className="absolute inset-0"><WebGLPoolBackground /></div>}
      {isCyber     && <div className="absolute inset-0"><WebGLCyberfluidBackground /></div>}
      {isMarble    && <div className="absolute inset-0"><WebGLLiquidMarbleBackground variant={marbleVariant} /></div>}
      {isMesh      && <div className="absolute inset-0"><WebGLMeshGradientBackground variant={meshVariant as any} /></div>}
      {isVideo && settings.background_video_url && (
        <div className="absolute inset-0">
          <VideoBackground
            url={settings.background_video_url}
            loop={settings.background_video_loop ?? true}
            fit={settings.background_video_fit ?? 'cover'}
            showSoundToggle={!(settings.background_video_muted ?? true)}
            accentColor={settings.text_color ?? undefined}
          />
          {settings.background_overlay_enabled && (
            <div className="absolute inset-0" style={{ background: getVideoOverlayStyle(settings) ?? undefined }} />
          )}
        </div>
      )}
      {showCssBg && (
        <div
          className={`absolute inset-0 ${presetAnim ?? ''}`}
          style={{ background: getBackgroundStyle(settings) }}
        />
      )}

      {/* Content column — same max-w-md width as bio links */}
      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 py-8 sm:py-10">
        <div className="w-full max-w-md">

          {/* Profile header strip */}
          <div className="flex items-center gap-2.5 mb-6 w-full">
            <Link
              to={backToBioPath}
              title={t("Voltar para bio", "Back to bio")}
              className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              style={textStyle}
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            {profile.avatar_url && !profileAvatarError ? (
              <img src={profile.avatar_url} alt={profile.display_name ?? ''} className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-black/5" onError={() => setProfileAvatarError(true)} />
            ) : (
              <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 ring-1 ring-black/5" style={{ background: `${btnBg}33`, color: btnBg }}>
                <Mail className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-tight truncate tracking-tight" style={textStyle}>{profile.display_name || `@${username}`}</p>
              {profile.bio && <p className="text-xs opacity-60 truncate leading-snug" style={textStyle}>{profile.bio}</p>}
            </div>
          </div>

          {/* Glass card */}
          <div className="rounded-3xl shadow-2xl overflow-hidden" style={glassCard}>

            {/* Card header */}
            <div
              className="px-5 pt-7 pb-5 sm:px-7 sm:pt-8 sm:pb-6 text-center"
              style={{ borderBottom: `1px solid ${settings.text_color}14` }}
            >
              <div
                className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-4 ring-1 ring-black/5"
                style={{ background: `${btnBg}22`, color: btnBg }}
              >
                <Mail className="h-5 w-5" />
              </div>
              <h1 className="text-[1.35rem] sm:text-[1.55rem] font-bold leading-[1.2] tracking-tight text-balance" style={textStyle}>
                {settings.email_collection_title || t("Vamos manter contato", "Let's stay in touch")}
              </h1>
              {settings.email_collection_description && (
                <p className="text-sm mt-2 opacity-70 max-w-xs mx-auto leading-relaxed text-balance" style={textStyle}>
                  {settings.email_collection_description}
                </p>
              )}
            </div>

            {/* Form */}
            <div className="px-5 py-4 sm:px-7 sm:py-5">
              <EmailCollectionForm
                settings={settings}
                userId={userId}
                showContainer={false}
                showIntro={false}
                stylePreset="solid"
                className="max-w-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-7">
            <a
              href="https://qlivar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wide uppercase opacity-40 hover:opacity-80 transition-opacity"
              style={textStyle}
            >
              {t("Desenvolvido com", "Powered by")}
              <img alt="Qlivar" className="h-3 w-3" src="/uploads/c60b833a-df7a-4878-bcf0-647598b01880.svg" />
              <span className="font-semibold">Qlivar</span>
            </a>
          </div>

        </div>
      </div>
    </motion.div>
  );
}
